<template>
  <section class="modal-ai-setup-avatar-section">
    <BaseSubHeading
      size="large"
      weight="500"
      class="modal-ai-setup-avatar-section__title"
    >
      Meet your AI {{ aiName }}
    </BaseSubHeading>

    <div class="modal-ai-setup-avatar-section__row">
      <button
        type="button"
        class="modal-ai-setup-avatar-section__arrow"
        aria-label="Previous avatar"
        @click="goPrev"
      >
        <icon
          data="@icon/chevron-with-paddings.svg"
          width="var(--icon-24)"
          height="var(--icon-24)"
          class="modal-ai-setup-avatar-section__arrow-icon modal-ai-setup-avatar-section__arrow-icon_left"
        />
      </button>

      <div class="modal-ai-setup-avatar-section__card">
        <Transition
          :name="avatarTransitionName"
          mode="out-in"
        >
          <img
            :key="selectedAvatar"
            :src="selectedAvatar"
            :alt="`AI avatar ${aiName}`"
            class="modal-ai-setup-avatar-section__image"
          />
        </Transition>
      </div>

      <button
        type="button"
        class="modal-ai-setup-avatar-section__arrow"
        aria-label="Next avatar"
        @click="goNext"
      >
        <icon
          data="@icon/chevron-with-paddings.svg"
          width="var(--icon-24)"
          height="var(--icon-24)"
          class="modal-ai-setup-avatar-section__arrow-icon"
        />
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue"

import BaseSubHeading from "@/components/Base/BaseSubHeading.vue"

defineProps({
  selectedAvatar: {
    type: String,
    default: ""
  },
  aiName: {
    type: String,
    default: ""
  }
})

const emit = defineEmits<{
  (e: "goPrev"): void
  (e: "goNext"): void
}>()

const avatarTransitionName = ref("avatar-slide-next")

const goPrev = () => {
  avatarTransitionName.value = "avatar-slide-prev"
  emit("goPrev")
}

const goNext = () => {
  avatarTransitionName.value = "avatar-slide-next"
  emit("goNext")
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/utils/variables/colors";
@import "@/assets/scss/utils/variables/fonts";
@import "@/assets/scss/utils/mixins/_flex";
@import "@/assets/scss/utils/typography-v2";

.modal-ai-setup-avatar-section {
  @include flex($direction: column, $align: center);

  width: 100%;
  padding-top: 56px;
  gap: var(--step4);

  @media (max-width: 1536px) {
    padding-top: 48px;
  }

  @media (max-width: 1280px) {
    padding-top: 40px;
  }

  &__title {
    @include body-large-500;

    margin: 0;
    color: var(--monochrome-07);
  }

  &__row {
    @include flex($justify: center, $align: center);

    width: 100%;
    gap: var(--step4);

    @media (max-width: 1280px) {
      gap: var(--step3);
    }
  }

  &__arrow {
    @include flex($justify: center, $align: center);

    width: 40px;
    height: 40px;
    border: 1px solid var(--monochrome-01);
    border-radius: 50%;
    box-shadow: var(--center-02);
    cursor: pointer;

    @media (max-width: 1536px) {
      width: 32px;
      height: 32px;
    }

    &:hover {
      background: var(--monochrome-01);

      .modal-ai-setup-avatar-section__arrow-icon {
        color: var(--monochrome-06);
      }
    }

    &:active {
      background: var(--monochrome-00);
      box-shadow: var(--center-01);
    }
  }

  &__arrow-icon {
    color: var(--monochrome-05);

    &_left {
      transform: rotate(180deg);
    }
  }

  &__card {
    @include flex($justify: center, $align: center);

    width: 208px;
    height: 208px;
    padding: var(--step4);
    border: 1px solid var(--monochrome-01);
    border-radius: 16px;
    box-shadow: var(--center-02);

    @media (max-width: 1536px) {
      width: 160px;
      height: 160px;
      padding: var(--step3);
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    border-radius: 10px;
    object-fit: cover;
  }

  .avatar-slide-next-enter-active,
  .avatar-slide-next-leave-active,
  .avatar-slide-prev-enter-active,
  .avatar-slide-prev-leave-active {
    transition:
      opacity 0.28s ease,
      transform 0.28s ease;
  }

  .avatar-slide-next-enter-from,
  .avatar-slide-prev-leave-to {
    opacity: 0;
    transform: translateX(20px) scale(0.98);
  }

  .avatar-slide-next-leave-to,
  .avatar-slide-prev-enter-from {
    opacity: 0;
    transform: translateX(-20px) scale(0.98);
  }
}

.dark {
  .modal-ai-setup-avatar-section {
    &__card {
      border-color: var(--monochrome-26);
      background: var(--monochrome-24);
    }

    &__arrow {
      border-color: var(--monochrome-26);
      background: var(--monochrome-24);

      &:hover {
        background: var(--monochrome-16);

        .modal-ai-setup-avatar-section__arrow-icon {
          color: #fff;
        }
      }

      &:active {
        background: var(--monochrome-24);
      }
    }

    &__arrow-icon {
      color: var(--monochrome-04);
    }
  }
}
</style>
